import { memo, type CSSProperties } from "react";
import { BOARD_SIZE, type ClientCell, type Team } from "@msf/shared";
import { FlagIcon } from "./icons.js";

const NUMBER_COLOR: Record<number, string> = {
  1: "#3d84c9",
  2: "#2f9e5f",
  3: "#e0524a",
  4: "#7a4fd0",
  5: "#b1533b",
  6: "#2aa79b",
  7: "#2b3440",
  8: "#8a8a8a",
};

interface CellProps {
  cell: ClientCell;
  x: number;
  y: number;
  justRevealed: boolean;
  /** Optimistically tapped, awaiting the server's reveal/error — see store/game.ts. */
  pending: boolean;
  /** This was the origin of the most recent move (either player's) — shown
   * with a persistent team-coloured ring so both players can see where the
   * game last moved, until the next reveal replaces it. */
  lastMove: boolean;
  lastMoveTeam: Team | null;
  focused: boolean;
  onTap: (x: number, y: number) => void;
}

function lastMoveRing(team: Team | null): CSSProperties {
  const color = team === "blue" ? "#5aa9e6" : "#f2685f";
  return { outline: `3px dashed ${color}`, outlineOffset: "-2px" };
}

// A handful of irregular fragment shapes so the crumble reads as an uneven
// break rather than a uniform tile split; each carries its own fall offset.
const SHARDS = [
  { clip: "polygon(0% 0%,58% 0%,42% 55%,0% 68%)", x: -9, y: 9, rot: -34 },
  { clip: "polygon(58% 0%,100% 0%,100% 48%,62% 58%)", x: 10, y: 5, rot: 30 },
  { clip: "polygon(0% 68%,42% 55%,55% 100%,0% 100%)", x: -13, y: 15, rot: -20 },
  { clip: "polygon(62% 58%,100% 48%,100% 100%,55% 100%)", x: 13, y: 17, rot: 24 },
  { clip: "polygon(42% 55%,62% 58%,55% 100%,35% 88%)", x: 1, y: 19, rot: 6 },
  { clip: "polygon(0% 0%,58% 0%,62% 44%,20% 38%)", x: -3, y: 3, rot: -10 },
];

function Shards({ variant }: { variant: "pending" | "fall" | "fade" }) {
  return (
    <div className={`shard-layer shard-${variant}`} aria-hidden="true">
      {SHARDS.map((s, i) => (
        <span
          key={i}
          className="shard"
          style={
            {
              clipPath: s.clip,
              "--shard-x": `${s.x}px`,
              "--shard-y": `${s.y}px`,
              "--shard-rot": `${s.rot}deg`,
              animationDelay: variant === "fall" ? `${i * 0.02}s` : undefined,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

function label(cell: ClientCell, x: number, y: number, pending: boolean): string {
  const pos = `column ${x + 1}, row ${y + 1}`;
  if (pending) return `Revealing, ${pos}`;
  if (cell.state === "hidden") return `Hidden tile, ${pos}`;
  if (cell.state === "mine") return `${cell.owner} mine, ${pos}`;
  return cell.adjacent === 0 ? `Empty, ${pos}` : `${cell.adjacent} adjacent mines, ${pos}`;
}

function CellBase({ cell, x, y, justRevealed, pending, lastMove, lastMoveTeam, focused, onTap }: CellProps) {
  const i = y * BOARD_SIZE + x;
  const aria = label(cell, x, y, pending);

  if (cell.state === "hidden") {
    return (
      <button
        type="button"
        data-cell={i}
        tabIndex={focused ? 0 : -1}
        aria-label={aria}
        aria-busy={pending}
        disabled={pending}
        onClick={() => onTap(x, y)}
        className="clay-btn relative aspect-square w-full touch-manipulation rounded-[22%] cell-focus-ring active:brightness-95 disabled:active:brightness-100"
      >
        {pending && <Shards variant="pending" />}
      </button>
    );
  }

  if (cell.state === "mine") {
    const tint = cell.owner === "red" ? "rgba(242,104,95,0.28)" : "rgba(90,169,230,0.28)";
    return (
      <div
        data-cell={i}
        role="gridcell"
        tabIndex={focused ? 0 : -1}
        aria-label={aria}
        className="relative flex aspect-square w-full items-center justify-center rounded-[22%] cell-focus-ring"
        style={{
          background: tint,
          boxShadow: "inset 2px 2px 5px rgba(0,0,0,0.12)",
          ...(lastMove ? lastMoveRing(lastMoveTeam) : {}),
        }}
      >
        <FlagIcon team={cell.owner} className={`h-[78%] w-[78%] ${justRevealed ? "animate-pop" : ""}`} />
        {justRevealed && <Shards variant="fall" />}
      </div>
    );
  }

  // safe
  return (
    <div
      data-cell={i}
      role="gridcell"
      tabIndex={focused ? 0 : -1}
      aria-label={aria}
      className={`relative flex aspect-square w-full items-center justify-center rounded-[22%] cell-focus-ring ${justRevealed ? "animate-pop" : ""}`}
      style={{
        background: "var(--revealed)",
        boxShadow: "inset 2px 2px 5px rgba(0,0,0,0.10), inset -1px -1px 3px rgba(255,255,255,0.25)",
        ...(lastMove ? lastMoveRing(lastMoveTeam) : {}),
      }}
    >
      {cell.adjacent > 0 && (
        <span
          className="text-[min(4.2vw,1.35rem)] font-extrabold leading-none"
          style={{ color: NUMBER_COLOR[cell.adjacent] }}
        >
          {cell.adjacent}
        </span>
      )}
      {justRevealed && <Shards variant="fade" />}
    </div>
  );
}

export const Cell = memo(CellBase);
